import React, { useState } from 'react';
import { Tooltip as MuiTooltip, ClickAwayListener, Box } from '@mui/material';

/**
 * Tooltip Props
 */
export interface TooltipProps {
  /** Tooltip content */
  content: React.ReactNode;
  /** Element that triggers the tooltip */
  children: React.ReactNode;
  /** Placement of tooltip */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Whether tooltip is controlled */
  open?: boolean;
  /** Callback when tooltip should close */
  onClose?: () => void;
  /** Callback when tooltip should open */
  onOpen?: () => void;
  /** Custom styles for tooltip */
  tooltipSx?: Record<string, any>;
  /** ARIA label */
  ariaLabel?: string;
}

/**
 * Tooltip - Atomic tooltip component
 * 
 * Generic tooltip wrapper around MUI Tooltip.
 * Handles both hover and click interactions.
 * 
 * Features:
 * - Desktop: Shows on hover
 * - Mobile: Shows on click/tap
 * - Click-away to dismiss
 * - Keyboard accessible
 * 
 * @example
 * ```tsx
 * <Tooltip content="Helpful information" placement="top">
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 */
export default function Tooltip({
  content,
  children,
  placement = 'top',
  open: controlledOpen,
  onClose,
  onOpen,
  tooltipSx,
  ariaLabel,
}: TooltipProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleTooltipClose = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
  };

  const handleTooltipOpen = () => {
    if (isControlled) {
      onOpen?.();
    } else {
      setInternalOpen(true);
    }
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isControlled) {
      open ? onClose?.() : onOpen?.();
    } else {
      setInternalOpen(prev => !prev);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box 
        component="span" 
        sx={{ display: 'inline-flex', alignItems: 'center' }}
      >
        <MuiTooltip
          title={content}
          arrow
          placement={placement}
          open={open}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
          disableFocusListener
          disableTouchListener
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'common.black',
                '& .MuiTooltip-arrow': {
                  color: 'common.black',
                },
                fontSize: '0.875rem',
                lineHeight: 1.4,
                padding: '0.75em 1em',
                maxWidth: '13em',
                ...tooltipSx,
              },
            },
          }}
        >
          <Box
            component="span"
            onClick={handleClick}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            role="button"
            aria-label={ariaLabel}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(e as unknown as React.MouseEvent);
              }
            }}
          >
            {children}
          </Box>
        </MuiTooltip>
      </Box>
    </ClickAwayListener>
  );
}
